/**
 * LazyTree — High-performance hierarchical tree with lazy loading
 * RULE: No full tree load. Root → expand → load children → cache.
 *
 * Props:
 *  onSelect(node) — called when leaf node clicked
 *  selectedId — currently selected node id
 *  searchText — filter visible nodes
 */
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { ChevronRight, ChevronDown, Loader } from 'lucide-react';
import { API_BASE as API, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}` });
const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 });

// Category color/label mapping
const CATEGORY_STYLE = {
  BS: { label: 'Balance Sheet', sections: [{ key: 'assets', label: 'Assets', filter: n => /asset|invest|misc/i.test(n.name), color: '#1e40af' }, { key: 'liabilities', label: 'Liabilities', filter: n => !/asset|invest|misc/i.test(n.name), color: '#7c3aed' }] },
  PL: { label: 'Profit & Loss', sections: [{ key: 'income', label: 'Income', filter: n => /income/i.test(n.name), color: '#15803d' }, { key: 'expenses', label: 'Expenses', filter: n => !/income/i.test(n.name), color: '#dc2626' }] },
};

// Single tree node — memoized to avoid re-renders
const TreeNode = memo(({ node, depth, expanded, onToggle, onSelect, selectedId, selectedIds, multiSelect, onMultiSelect, childrenCache, loadingNodes, searchText }) => {
  const isExp = expanded.has(node.id);
  const isLoading = loadingNodes.has(node.id);
  const isSelected = multiSelect ? selectedIds?.has(node.id) : node.id === selectedId;
  const isLeaf = !node.hasChildren;
  const children = childrenCache.get(node.id) || [];

  if (searchText && isLeaf && !node.name.toLowerCase().includes(searchText.toLowerCase())) return null;

  const indent = 12 + depth * 16;
  const nodeColor = depth === 0 ? (node.category === 'BS' ? '#1e40af' : '#15803d') : depth === 1 ? '#374151' : isSelected ? '#1d4ed8' : '#4b5563';

  const handleClick = () => {
    if (multiSelect) {
      if (isLeaf) onMultiSelect(node.id, node.name);
      else onToggle(node);
    } else {
      if (isLeaf) onSelect(node);
      else { onToggle(node); onSelect(node); }
    }
  };

  return (
    <div>
      <div
        onClick={handleClick}
        style={{
          padding: `4px 8px 4px ${indent}px`,
          fontSize: depth === 0 ? 13 : 12,
          fontWeight: depth <= 1 ? (depth === 0 ? 700 : 600) : (isSelected ? 700 : 400),
          cursor: 'pointer', color: nodeColor,
          display: 'flex', alignItems: 'center', gap: 4,
          background: isSelected ? '#dbeafe' : 'transparent',
          borderRadius: 4, marginLeft: 4, marginRight: 4, transition: 'background 0.1s',
        }}
        onMouseEnter={e => { if (!e.currentTarget) return; if (!isSelected) e.currentTarget.style.background = '#f3f4f6'; }}
        onMouseLeave={e => { if (!e.currentTarget) return; if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
        data-testid={`tree-node-${node.id}`}
      >
        {multiSelect && isLeaf && <input type="checkbox" checked={isSelected} onChange={() => onMultiSelect(node.id, node.name)} onClick={e => e.stopPropagation()} style={{ width: 14, height: 14, flexShrink: 0 }} />}
        {!isLeaf && (isLoading ? <Loader size={11} className="animate-spin" style={{ color: '#6b7280', flexShrink: 0 }} /> : isExp ? <ChevronDown size={depth === 0 ? 13 : 11} style={{ flexShrink: 0 }} /> : <ChevronRight size={depth === 0 ? 13 : 11} style={{ flexShrink: 0 }} />)}
        {!multiSelect && isLeaf && <span style={{ width: 11, flexShrink: 0 }} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{node.name}</span>
        {isLeaf && node.balance !== undefined && <span style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'var(--erp-font-mono)', flexShrink: 0 }}>{fmtC(Math.abs(node.balance))}</span>}
        {!isLeaf && node.childCount > 0 && <span style={{ fontSize: 9, color: '#9ca3af', flexShrink: 0 }}>({node.childCount})</span>}
      </div>
      {isExp && children.map(child => (
        <TreeNode key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} onSelect={onSelect} selectedId={selectedId} selectedIds={selectedIds} multiSelect={multiSelect} onMultiSelect={onMultiSelect} childrenCache={childrenCache} loadingNodes={loadingNodes} searchText={searchText} />
      ))}
    </div>
  );
});

const LazyTree = ({ onSelect, selectedId, searchText = '', multiSelect = false, selectedIds, onMultiSelect }) => {
  const [rootNodes, setRootNodes] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [childrenCache, setChildrenCache] = useState(new Map());
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  const [rootLoading, setRootLoading] = useState(false);

  // Load root nodes on mount
  useEffect(() => {
    (async () => {
      setRootLoading(true);
      try {
        const r = await fetch(`${API}/api/financial-statements/tree?level=root`, { headers: hdrs() });
        const j = await r.json();
        if (j.success) setRootNodes(j.data || []);
      } catch {}
      setRootLoading(false);
    })();
  }, []);

  const handleToggle = useCallback(async (node) => {
    const id = node.id;
    if (expanded.has(id)) {
      setExpanded(prev => { const n = new Set(prev); n.delete(id); return n; });
      return;
    }
    // Expand
    setExpanded(prev => new Set(prev).add(id));
    // Load children if not cached
    if (!childrenCache.has(id) && node.hasChildren) {
      setLoadingNodes(prev => new Set(prev).add(id));
      try {
        const childLevel = node.type === 'primaryGroup' ? 'primaryGroup' : node.type === 'group' ? 'group' : 'primaryGroup';
        const r = await fetch(`${API}/api/financial-statements/tree?level=${childLevel}&parentId=${id}`, { headers: hdrs() });
        const j = await r.json();
        if (j.success) setChildrenCache(prev => new Map(prev).set(id, j.data || []));
      } catch {}
      setLoadingNodes(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [expanded, childrenCache]);

  // Group root nodes by category section
  const sections = useMemo(() => {
    const bsNodes = rootNodes.filter(n => n.category === 'BS');
    const plNodes = rootNodes.filter(n => n.category !== 'BS');
    return [
      ...CATEGORY_STYLE.BS.sections.map(s => ({ ...s, nodes: bsNodes.filter(s.filter) })),
      ...CATEGORY_STYLE.PL.sections.map(s => ({ ...s, nodes: plNodes.filter(s.filter) })),
    ].filter(s => s.nodes.length > 0);
  }, [rootNodes]);

  if (rootLoading) return <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}><Loader size={16} className="animate-spin" style={{ display: 'inline', marginRight: 6 }} />Loading accounts...</div>;

  return (
    <div style={{ flex: 1, overflow: 'auto' }} data-testid="lazy-tree">
      {sections.map(sec => (
        <div key={sec.key}>
          <div style={{ padding: '6px 8px', fontSize: 10, fontWeight: 800, color: sec.color, textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>{sec.label}</div>
          {sec.nodes.map(node => (
            <TreeNode key={node.id} node={node} depth={0} expanded={expanded} onToggle={handleToggle} onSelect={onSelect} selectedId={selectedId} selectedIds={selectedIds} multiSelect={multiSelect} onMultiSelect={onMultiSelect} childrenCache={childrenCache} loadingNodes={loadingNodes} searchText={searchText} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default LazyTree;
